import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, FileText, AlertCircle } from 'lucide-react';
import { publicAPI } from '../services/api';
import { InvoiceDoc, CompanyProfileDoc } from '../components/documents/types';
import InvoicePrintable from '../components/documents/InvoicePrintable';
import { downloadPdf } from '../utils/downloadPdf';

const PublicInvoice: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [invoice, setInvoice] = useState<InvoiceDoc | null>(null);
  const [company, setCompany] = useState<CompanyProfileDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicAPI.getInvoice(token)
      .then((res) => { setInvoice(res.data.invoice); setCompany(res.data.company); })
      .catch((e) => {
        const s = e.response?.status;
        setError(
          s === 410 ? 'This invoice link has expired. Please ask the sender for a new one.'
          : s === 404 ? 'This invoice link is invalid or unavailable.'
          : 'Failed to load invoice.'
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async () => {
    if (!invoice) return;
    setGenerating(true);
    try { await downloadPdf('invoice-content', `Invoice-${invoice.invoiceNumber}.pdf`); }
    finally { setGenerating(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
    </div>
  );
  if (error || !invoice) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-4">
      <AlertCircle className="h-12 w-12 text-rose-500 mb-4" />
      <p className="text-slate-700 text-lg">{error || 'Invoice not found'}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center space-x-2 text-slate-800 font-semibold">
            <FileText className="h-5 w-5 text-indigo-600" />
            <span>Invoice {invoice.invoiceNumber}</span>
          </div>
          <button
            onClick={handleDownload}
            disabled={generating}
            className="flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:opacity-90 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            <span>{generating ? 'Generating…' : 'Download PDF'}</span>
          </button>
        </div>
        <InvoicePrintable invoice={invoice} company={company} />
      </div>
    </div>
  );
};

export default PublicInvoice;
